/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';

import { useTranslation } from 'react-i18next';
import type { Site } from '@hotjar/site';
import { useForm } from 'react-hook-form';
import { validateHTTPSUrl } from '@hotjar/insights-common/lib/url';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { Button } from '@hotjar/hotsauce';

import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { bus, refreshData } from '@hotjar/webapp/utils/events';
import {
  Label,
  Section,
  Description,
  StyledInput as Input,
  StyledSelect as Select,
} from '../styles';
import { siteApi } from '../api';

const industrySectors: Array<string> = [
  'e_commerce',
  'saas',
  'marketplace',
  'consulting',
  'media',
  'blog_or_personal',
  'education',
  'community',
  'non_profit',
  'other',
];

const InputsCss = css`
  margin-top: 8px;
  margin-bottom: 32px;
  text-transform: none;
  font-weight: normal;
`;

interface SiteInformationsProps {
  site: Site;
  onFormDirty: (isDirty: boolean) => void;
  onFormSaved: () => void;
}

type FormResult = {
  name: Site['name'];
  url: Site['url'];
  industry: Site['industry'];
  legal_name: Site['legalName'];
  privacy_policy_url: Site['privacyPolicyUrl'];
};

export const SiteInformations = ({
  site,
  onFormDirty,
  onFormSaved,
}: SiteInformationsProps): JSX.Element => {
  const { t } = useTranslation();
  const { success, error } = useNotify();
  const { register, handleSubmit, errors, reset, formState } = useForm<FormResult>({
    mode: 'onBlur',
  });

  const { isDirty, isSubmitting } = formState;
  const [canSubmit, setCanSubmit] = useState(true);

  const { hasPermission } = useAuthorization();
  const hasSitesUpdatePermission = hasPermission('site', `${site.id}`, 'sites-update');

  useEffect(() => {
    // on load and when site changes, we set values to the ones from the site.
    reset({
      name: site.name,
      url: site.url,
      industry: site.industry,
      legal_name: site.legalName,
      privacy_policy_url: site.privacyPolicyUrl,
    });
    setCanSubmit(true);
  }, [site, reset]);

  useEffect(() => {
    onFormDirty(isDirty);
  }, [isDirty, onFormDirty]);

  const onSuccess = () => {
    onFormSaved();
    success(t('common:site-settings.site-information.save-success'));
  };

  const onSubmit = async (data: FormResult) => {
    setCanSubmit(false);
    const response = await siteApi.put(
      site.account.id as number,
      site.id,
      'site.information',
      data,
    );

    if (response.success) {
      const hasRefreshDataListener = bus.emitter.listeners(refreshData.eventType).length > 0;

      if (hasRefreshDataListener) {
        bus.publish(refreshData(() => onSuccess()));
      } else {
        onSuccess();
      }
    } else {
      setCanSubmit(true);
      error(t('common:site-settings.site-information.save-error'));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Section>
        <Label htmlFor="site-informations-name">
          {t('common:site-settings.site-information.site-name')}
        </Label>
        <Input
          id="site-informations-name"
          name="name"
          placeholder={t('common:site-settings.site-information.site-name-placeholder')}
          inputRef={register({ required: true })}
          errorMessage={t('common:site-settings.site-information.site-name-error')}
          isError={!!errors.name}
          disabled={!hasSitesUpdatePermission}
          containerCss={InputsCss}
        />
        <Label htmlFor="site-informations-url">
          {t('common:site-settings.site-information.site-url')}
        </Label>
        <Input
          id="site-informations-url"
          name="url"
          placeholder={t('common:site-settings.site-information.site-url-placeholder')}
          inputRef={register({ required: true, validate: (value) => validateHTTPSUrl(value) })}
          errorMessage={t('common:site-settings.site-information.site-url-error')}
          isError={!!errors.url}
          disabled={!hasSitesUpdatePermission}
          containerCss={InputsCss}
        />
        <Label htmlFor="site-informations-industry">
          {t('common:site-settings.site-information.industry-sector')}
        </Label>
        <Select
          id="site-informations-industry"
          name="industry"
          ref={register({ required: true })}
          isError={!!errors.industry}
          errorMessage={t('common:site-settings.site-information.industry-sector-error')}
          disabled={!hasSitesUpdatePermission}
          containerCss={InputsCss}
        >
          <option value="">
            {t('common:site-settings.site-information.industry-sector-options.default')}
          </option>
          {industrySectors.map((industry) => (
            <option value={industry} key={industry}>
              {t(`common:site-settings.site-information.industry-sector-options.${industry}`)}
            </option>
          ))}
        </Select>
        <Label htmlFor="site-informations-legal_name">
          {t('common:site-settings.site-information.legal-name')}
        </Label>
        <Description>
          {t('common:site-settings.site-information.legal-name-description')}
        </Description>
        <Input
          id="site-informations-legal_name"
          name="legal_name"
          placeholder={t('common:site-settings.site-information.legal-name-placeholder')}
          inputRef={register}
          disabled={!hasSitesUpdatePermission}
          containerCss={InputsCss}
        />
        <Label htmlFor="site-informations-privacy_policy_url">
          {t('common:site-settings.site-information.privacy-policy')}
          <Description>
            {t('common:site-settings.site-information.privacy-policy-description')}
          </Description>
        </Label>
        <Input
          id="site-informations-privacy_policy_url"
          name="privacy_policy_url"
          placeholder={t('common:site-settings.site-information.privacy-policy-placeholder')}
          inputRef={register({ validate: (value) => value === '' || validateHTTPSUrl(value) })}
          errorMessage={t('common:site-settings.site-information.privacy-policy-error')}
          isError={!!errors.privacy_policy_url}
          disabled={!hasSitesUpdatePermission}
          containerCss={InputsCss}
        />
        <Tooltip
          enabled={!hasSitesUpdatePermission}
          content={t('common:site-settings.no-edit-access') as string}
          placement="right"
        >
          <div
            css={css`
              display: inline-block;
            `}
          >
            <Button
              type="submit"
              isDisabled={!hasSitesUpdatePermission || isSubmitting || !isDirty || !canSubmit}
              isLoading={!canSubmit}
              loadingText={t('common:site-settings.saving-changes')}
            >
              {t('common:site-settings.save-changes')}
            </Button>
          </div>
        </Tooltip>
      </Section>
    </form>
  );
};
